<template>
  <div class="card mt-10">
    <div class="card-body">
      <div class="mb-5 w-lg-25 w-md-50 w-sm-100"></div>
      <div class="row">
        <div class="col-12">
          <div
            class="d-flex gap-10"
            data-kt-buttons="true"
            data-kt-buttons-target=".form-check-image, .form-check-input"
          >
            <!--begin::Col-->
            <div v-for="option in exportOptions" :key="option.value" class="">
              <label
                class="form-check-image border rounded"
                :class="exportFormat === option.value ? 'border-primary' : ''"
              >
                <div
                  class="form-check-wrapper format-option d-flex flex-column align-items-center justify-content-center"
                  :class="
                    option.disabled || exportRequestForSurveyLoading
                      ? 'disabled'
                      : ''
                  "
                >
                  <i
                    class="pt-5 button-icon bi"
                    :class="`${exportFormat === option.value ? 'text-primary' : 'text-muted'} ${exportFormatIconMap[option.value]}`"
                  ></i>
                  <div class="mt-3">{{ option.title }}</div>
                </div>

                <div class="form-check form-check-custom form-check-solid">
                  <input
                    name="export-format"
                    type="checkbox"
                    :value="option.value"
                    class="form-check-input d-none"
                    :checked="exportFormat === option.value"
                    :disabled="option.disabled || exportRequestForSurveyLoading"
                    @click="exportFormat = option.value"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-5 mt-5">
        <PulsFormCheck
          v-model="shouldExportWithHierarchy"
          :label="$t('groupsComparison.filter.shouldExportWithHierarchy')"
          :disabled="
            selectedFormat.value === 'pdf' ||
            selectedFormat.value === 'pptx' ||
            exportRequestForSurveyLoading ||
            hasSegmentSelected
          "
        />
        <PulsFormCheck
          v-if="userIsAdmin"
          v-model="includeSensitiveQuestions"
          :label="$t('resultDetails.exportModal.includeSensitiveQuestions')"
          :disabled="exportRequestForSurveyLoading"
        />
      </div>

      <div
        v-if="exportRequestForSurveyLoading"
        class="d-flex flex-column align-items-center"
      >
        <span v-if="shouldExportMultipleGroups" class="mb-5">
          {{ $t('resultDetails.exportModal.loadingMessage') }}
        </span>
        <LoadingSpinner :active="true" spinner="bar-fade-scale" class="mt-5" />
      </div>
      <div v-if="exportRequestForSurveyError" class="alert alert-danger mt-5">
        {{ exportRequestForSurveyError }}
      </div>
      <div v-if="!exportRequestForSurveyLoading" class="col-6 mt-5 mb-5">
        <div v-if="selectedFormat && validForm" class="alert alert-info">
          {{ selectedFormat.description }}
        </div>
      </div>
      <button
        class="btn btn-sm btn-primary"
        :disabled="!validForm || exportRequestForSurveyLoading"
        @click="exportData"
      >
        {{ $t('resultDetails.exportData') }}
      </button>
    </div>
  </div>
</template>

<script>
import { useExportStore } from '@/stores/export-store'
import { useUserStore } from '@/stores/user-store'
import { useResultsStore } from '@/stores/results-store'

import { allChildGroupsRecursively } from '@/helpers/results-helper'

export default {
  name: 'DataExporter',
  components: {},
  props: {
    organizationGroup: {
      type: Object,
      default: () => {}
    },
    surveyName: {
      type: String,
      default: ''
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    resultViewForUser: {
      type: Object,
      default: () => {}
    },
    selectedGroup: {
      type: Object,
      default: () => {}
    },
    selectedAttributeSegment: {
      type: Object,
      default: () => {}
    },
    groups: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const exportStore = useExportStore()
    const userStore = useUserStore()
    const resultsStore = useResultsStore()
    return {
      exportStore,
      userStore,
      resultsStore
    }
  },
  data() {
    return {
      customLogoData: null,
      customLogoDimensions: null,

      exportMethod: 'download',
      exportMethodIconMap: {
        email: 'bi-envelope-plus',
        download: 'bi-download'
      },
      exportFormat: 'xlsx',
      shouldExportWithHierarchy: false,
      includeSensitiveQuestions: false,
      exportFormatIconMap: {
        xlsx: 'bi-filetype-xlsx',
        json: 'bi-filetype-json',
        pptx: 'bi-filetype-pptx',
        pdf: 'bi-filetype-pdf ms-2'
      }
    }
  },
  computed: {
    hasSegmentSelected() {
      return this.selectedAttributeSegment.name !== 'all'
    },
    exportOptions() {
      return [
        {
          value: 'xlsx',
          title: this.$t('resultDetails.exportModal.saveAsExcelButton'),
          description: this.$t(
            this.shouldExportMultipleGroups
              ? 'resultDetails.exportModal.saveAsExcelAllGroupsDescription'
              : 'resultDetails.exportModal.saveAsExcelDescription',
            { groupTitle: this.groupTitle, segmentInfo: this.segmentInfoString }
          )
        },

        {
          value: 'pptx',
          title: this.$t('resultDetails.exportModal.saveAsPowerPointButton'),
          description: this.$t(
            'resultDetails.exportModal.saveAsPowerPointDescription',
            { groupTitle: this.groupTitle, segmentInfo: this.segmentInfoString }
          ),
          disabled: this.shouldExportMultipleGroups
        },
        {
          value: 'pdf',
          title: this.$t('resultDetails.exportModal.saveAsPdfButton'),
          description: this.$t(
            'resultDetails.exportModal.saveAsPdfDescription',
            { groupTitle: this.groupTitle, segmentInfo: this.segmentInfoString }
          ),
          disabled: this.shouldExportMultipleGroups
        }
      ].filter((o) => {
        return (
          o.value !== 'pdf' ||
          this.surveyDetails?.details?.config?.variant === 'feelgood'
        ) // only PDF export for Feelgood
      })
    },
    segmentInfoString() {
      if (this.hasSegmentSelected) {
        return ` (${this.selectedAttributeSegment.attributeTitle}: ${this.selectedAttributeSegment.segmentTitle})`
      }
      return ''
    },
    exportRequestForSurveySuccess() {
      return this.exportStore.exportRequestForSurveySuccess
    },
    exportRequestForSurveyLoading() {
      return this.exportStore.exportRequestForSurveyLoading
    },
    exportRequestForSurveyError() {
      return this.exportStore.exportRequestForSurveyError
    },
    email() {
      if (this.exportMethod === 'email') {
        const surveyTitle = this.$tr(
          this.surveyDetails.details.messages,
          'title'
        )
        return {
          subject: this.$t('export.email.subject', {
            surveyTitle
          }),
          body: this.$t('export.email.body', { surveyTitle })
        }
      }
      return null
    },
    groupTitle() {
      return this.$tr(this.selectedGroup.messages, 'title')
    },
    exportParams() {
      const groupName = this.shouldExportMultipleGroups
        ? 'all'
        : this.selectedGroup.name

      const params = {
        groupName,
        groupTitle: this.groupTitle,
        exportMethod: this.exportMethod,
        surveyName: this.surveyName,
        sortedGroupNames: this.shouldExportMultipleGroups
          ? this.filteredAndSortedGroupNames
          : [],
        exportFormat: this.exportFormat,
        email: this.email,
        includeSensitiveQuestions: this.includeSensitiveQuestions,
        theme: this.theme,
        resultViewForUser: this.resultViewForUser,
        includeHistory: !!this.surveyNames,
        customLogoData: this.customLogoData,
        customLogoDimensions: this.customLogoDimensions,
        participationSettings: this.participationSettings,
        shouldExportMultipleGroupsForEntireOrganization:
          this.shouldExportMultipleGroups &&
          this.selectedGroup.name === this.organizationGroup.name
      }
      if (this.customLogoData && this.customLogoDimensions) {
        params.customLogo = {
          data: this.customLogoData,
          dimensions: this.customLogoDimensions
        }
      }
      if (this.hasSegmentSelected && !this.shouldExportMultipleGroups) {
        params.filter = {
          attributeName: this.selectedAttributeSegment.attributeName,
          segmentName: this.selectedAttributeSegment.segmentName,
          attributeTitle: this.selectedAttributeSegment.attributeTitle,
          segmentTitle: this.selectedAttributeSegment.segmentTitle
        }
      }
      return params
    },
    organizationLogoUrl() {
      return this.userStore.getUserInfo.organizationLogoUrl
    },
    shouldExportMultipleGroups() {
      return this.shouldExportWithHierarchy && this.groups.length > 1
    },
    filteredAndSortedGroupNames() {
      const allChildGroupNamesForSelectedGroup = this.shouldExportWithHierarchy
        ? allChildGroupsRecursively({
            group: this.selectedGroup,
            groups: this.groups
          }).map((g) => g.name)
        : []

      return this.groups
        .filter((group) => {
          // only include selected and and child groups
          if (this.shouldExportWithHierarchy) {
            return (
              group.name === this.selectedGroup.name ||
              allChildGroupNamesForSelectedGroup.includes(group.name)
            )
          } else {
            return true
          }
        })
        .map((group) => {
          return group.name
        })
    },
    surveyNames() {
      if (
        this.surveyDetails.oldSurveysInfo.length &&
        this.exportFormat === 'pptx'
      ) {
        // only support for history in pptx
        return this.surveyDetails.oldSurveysInfo
          .map((s) => s.name)
          .concat(this.surveyName)
      } else {
        return null
      }
    },
    selectedFormat() {
      return this.exportOptions.find((e) => e.value === this.exportFormat)
    },
    userIsAdmin() {
      return this.userStore.userIsPulsAdmin || this.userStore.userIsGroupAdmin
    },
    validForm() {
      return (
        (this.exportFormat && this.exportFormat === 'xlsx') ||
        !this.shouldExportMultipleGroups
      )
    },
    participationSettings() {
      if (this.surveyDetails.details.resultViews.participation) {
        return this.surveyDetails.details.resultViews.participation
      } else {
        return null
      }
    },
    theme() {
      const orgData = this.userStore.getOrgConfig
      if (orgData?.orgSettings?.organizationBrandColor) {
        return {
          name: this.surveyDetails.details.config?.theme || 'default',
          mainColor: orgData.orgSettings.organizationBrandColor
        }
      } else if (this.surveyDetails.details.config?.theme) {
        return {
          name: this.surveyDetails.details.config?.theme
        }
      }
      return null
    }
  },
  watch: {
    shouldExportWithHierarchy(exportAllGroups) {
      if (
        exportAllGroups &&
        (this.selectedFormat.value === 'pptx' ||
          this.selectedFormat.value === 'pdf')
      ) {
        this.exportFormat = this.exportOptions.filter(
          (o) => !o.disabled
        )[0].value // select first non-disabled format
      }
    },
    selectedAttributeSegment() {
      if (this.hasSegmentSelected) {
        this.shouldExportWithHierarchy = false
      }
    }
  },
  mounted() {
    this.fetchAndSaveLogo()
  },
  methods: {
    fetchAndSaveLogo() {
      if (this.organizationLogoUrl) {
        fetch(this.organizationLogoUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const reader = new FileReader()
            reader.readAsDataURL(blob)
            reader.onloadend = () => {
              const dataUrl = reader.result
              this.customLogoData = dataUrl

              // Create an image element to determine natural dimensions
              const img = new Image()
              img.onload = () => {
                const naturalWidth = img.naturalWidth
                const naturalHeight = img.naturalHeight
                // Optionally save dimensions to your component's data
                this.customLogoDimensions = {
                  width: naturalWidth,
                  height: naturalHeight
                }
              }
              img.src = dataUrl
            }
          })
          .catch((error) => console.error('Failed to fetch logo', error))
      }
    },
    async exportData() {
      this.exportStore.requestExportForSurveyAction(this.exportParams)
    }
  }
}
</script>

<style lang="scss" scoped>
.flex-even {
  flex: 1 1 !important;
}
.button-icon {
  font-size: 3rem !important;
}

.format-option {
  min-width: 200px;
  height: 80px;
  > i {
    transition: color 0.2s ease;
  }
}
.border-primary {
  border-color: rgba(
    var(--kt-primary-rgb),
    var(--bs-border-opacity)
  ) !important;
}

.form-check-wrapper.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
